import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Leaf, Zap, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ProjectCard = ({ project }) => {
  const Icon = project.icon;
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="group bg-brand-surface rounded-2xl overflow-hidden card-hover"
      data-testid={`project-card-${project.id}`}
    >
      <div className="relative h-64 overflow-hidden">
        <img 
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full">
            <Icon className="w-4 h-4 text-brand-green" />
            <span className="font-sans text-xs font-medium text-brand-dark">
              {project.category}
            </span>
          </span>
        </div>

        <div className="absolute bottom-4 left-4">
          <p className="font-sans text-sm text-white/80">{project.client}</p>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <h2 className="font-serif text-xl md:text-2xl text-brand-dark mb-3 group-hover:text-brand-green transition-colors">
          {project.title}
        </h2>
        <p className="font-sans text-gray-600 text-sm md:text-base mb-6 leading-relaxed">
          {project.description}
        </p>

        <div className="flex flex-wrap gap-2">
          {project.tags.split(', ').map((tag) => (
            <span 
              key={tag}
              className="font-sans text-xs px-3 py-1 bg-white rounded-full text-gray-600 border border-gray-100"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
};

const ProjectsPage = () => {
  const projectsData = [
    {
      id: 1,
      title: 'ESG Strategy & Integration',
      client: 'Major New Zealand-based Client',
      category: 'International',
      description: 'Comprehensive ESG strategy development and integration for a leading international organization, aligning with global sustainability standards.',
      icon: Globe,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
      tags: 'ESG Integration, Strategy, International'
    },
    {
      id: 2,
      title: 'Biochar Carbon Project',
      client: 'Carbon Removal Initiative',
      category: 'Carbon Removal',
      description: 'Development of a biochar-based carbon removal project, creating verified carbon credits while improving soil health and agricultural productivity.',
      icon: Leaf,
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&q=80',
      tags: 'Carbon Credits, Biochar, Agriculture'
    },
    {
      id: 3,
      title: 'Green Hydrogen Plant Setup',
      client: 'Clean Energy Infrastructure',
      category: 'Clean Energy',
      description: "Supporting the setup of a green hydrogen production facility, contributing to India's clean energy transition and decarbonization goals.",
      icon: Zap,
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80',
      tags: 'Green Hydrogen, Clean Energy, Infrastructure'
    },
    {
      id: 4,
      title: 'Academic Institute Partnership',
      client: 'Knowledge Partnership',
      category: 'Education',
      description: 'Serving as the official academic supporting partner, developing sustainability curriculum and training programs for future ESG professionals.',
      icon: GraduationCap,
      image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80',
      tags: 'Education, Training, Knowledge Transfer'
    }
  ];

  return (
    <main data-testid="projects-page" className="pt-20">
      <section className="py-20 md:py-32 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">
              Our Work
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-dark mt-4 mb-6">
              Selected Projects & Partnerships
            </h1>
            <p className="font-sans text-base md:text-lg text-gray-600 leading-relaxed">
              Explore our diverse portfolio of sustainability initiatives, from international 
              ESG integrations to cutting-edge carbon removal projects.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projectsData.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark mb-6">
              Impact Across Sectors
            </h2>
            <p className="font-sans text-gray-600 max-w-2xl mx-auto">
              Our work spans multiple industries and geographies, delivering measurable 
              sustainability outcomes.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center p-6 bg-white rounded-xl"
            >
              <p className="font-serif text-2xl text-brand-dark mb-2">Clean Energy</p>
              <p className="font-sans text-sm text-gray-600">Infrastructure</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center p-6 bg-white rounded-xl"
            >
              <p className="font-serif text-2xl text-brand-dark mb-2">Financial Services</p>
              <p className="font-sans text-sm text-gray-600">ESG Integration</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center p-6 bg-white rounded-xl"
            >
              <p className="font-serif text-2xl text-brand-dark mb-2">Agriculture</p>
              <p className="font-sans text-sm text-gray-600">Carbon Removal</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center p-6 bg-white rounded-xl"
            >
              <p className="font-serif text-2xl text-brand-dark mb-2">Education</p>
              <p className="font-sans text-sm text-gray-600">Knowledge Transfer</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 cta-gradient">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark mb-6">
              Want to Be Our Next Success Story?
            </h2>
            <p className="font-sans text-gray-600 max-w-2xl mx-auto mb-10">
              Let's discuss how we can help transform your sustainability journey.
            </p>
            <Link to="/contact">
              <Button 
                data-testid="projects-cta-button"
                className="btn-gradient text-white font-sans font-medium px-8 py-6 text-base"
              >
                Start a Conversation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ProjectsPage;
